import {
    Box,
    Button,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    VStack
} from '@chakra-ui/react';
import { serverTimestamp, useFirebaseApp } from '@lib/firebase';
import { intl_html_values } from '@lib/localization/intl';
import { Formik } from 'formik';
import React, { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const getEfnNumberLocalizedString = (user_country: string) => {
    let efn_number_localized = (
        <FormattedMessage id="user.efn_number.default" defaultMessage="Fortbildungsnummer" />
    );

    switch (user_country) {
        case 'AT':
            efn_number_localized = (
                <FormattedMessage id="user.efn_number.AT" defaultMessage="Fortbildungsnummer" />
            );
            break;
        case 'DE':
            efn_number_localized = (
                <FormattedMessage id="user.efn_number.DE" defaultMessage="Fortbildungsnummer" />
            );
            break;
        case 'CH':
            efn_number_localized = (
                <FormattedMessage id="user.efn_number.CH" defaultMessage="Fortbildungsnummer" />
            );
            break;
        case 'UK':
            efn_number_localized = (
                <FormattedMessage id="user.efn_number.UK" defaultMessage="Fortbildungsnummer" />
            );
            break;
    }

    return efn_number_localized;
};

const UserEfnNumberModal = () => {
    const [submitError, setSubmitError] = useState<string | null>(null);
    const initialFocusRef = useRef<HTMLInputElement>(null);
    const { userData, userDataDocRef } = useFirebaseApp();

    if (!userData || !userDataDocRef) {
        return null;
    }

    const efn_number_country =
        userData.country && ['AT', 'DE', 'CH', 'UK'].includes(userData.country)
            ? userData.country
            : '';

    const efn_number_localized = getEfnNumberLocalizedString(efn_number_country);

    return (
        <Modal
            isOpen={true}
            onClose={() => {
                return false;
            }}
            size="lg"
            colorScheme="blue">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader />
                <ModalBody>
                    <Box px="20px">
                        <Formik
                            initialValues={{
                                efn_number: userData.efn_number || ''
                            }}
                            onSubmit={async (values) => {
                                setSubmitError(null);

                                try {
                                    await userDataDocRef?.set(
                                        {
                                            efn_number: values.efn_number,
                                            updated_at: serverTimestamp(),
                                            updated_efn_number_at: serverTimestamp()
                                        },
                                        { merge: true }
                                    );
                                } catch (err) {
                                    setSubmitError(err.message);
                                }
                            }}>
                            {({
                                values,
                                handleChange,
                                handleBlur,
                                handleSubmit,
                                isSubmitting,
                                submitForm
                            }) => (
                                <form onSubmit={handleSubmit}>
                                    <VStack align="stretch" spacing="4">
                                        <Box>
                                            <FormControl id="efn_number" width="full" isRequired>
                                                <FormLabel>
                                                    <FormattedMessage
                                                        id="user.efn_number.label"
                                                        defaultMessage="Ihre {efn_number_localized}"
                                                        values={{
                                                            ...intl_html_values,
                                                            efn_number_localized
                                                        }}
                                                    />
                                                </FormLabel>
                                                <Input
                                                    ref={initialFocusRef}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.efn_number}
                                                />
                                            </FormControl>
                                        </Box>
                                        <Box textAlign="center">{submitError}</Box>
                                        <HStack pt="2">
                                            <Button
                                                type="submit"
                                                disabled={isSubmitting}
                                                colorScheme="blue">
                                                <FormattedMessage
                                                    id="common.button_save"
                                                    defaultMessage="Speichern"
                                                    values={{
                                                        ...intl_html_values
                                                    }}
                                                />
                                            </Button>
                                            <Button
                                                disabled={isSubmitting}
                                                onClick={() => {
                                                    values.efn_number = 'Keine Fortbildungsnummer';
                                                    submitForm();
                                                }}>
                                                <FormattedMessage
                                                    id="user.efn_number_modal.button_no_efn_number"
                                                    defaultMessage="Keine {efn_number_localized}"
                                                    values={{
                                                        ...intl_html_values,
                                                        efn_number_localized
                                                    }}
                                                />
                                            </Button>
                                        </HStack>
                                    </VStack>
                                </form>
                            )}
                        </Formik>
                    </Box>
                </ModalBody>
                <ModalFooter />
            </ModalContent>
        </Modal>
    );
};

export default UserEfnNumberModal;
